import React, { useRef, useEffect, useState, useCallback } from "react";
import * as THREE from "three";
import styles from "./HeroView.module.css";
import heroModelData from "../../assets/heroModel.json";
import exportIconLight from "../../assets/export_active_dark.svg";
import exportIconDark from "../../assets/export_active_light.svg";
import shareIconLight from "../../assets/share_light.svg";
import shareIconDark from "../../assets/share_dark.svg";
import heroModelUrl from "../../assets/hero-model.stl?url";

interface HeroViewProps {
  theme: "light" | "dark";
  onNavigateToSignUp: () => void;
  onSelectionChange?: (selection: any[]) => void;
  onMeasurementChange?: (measurement: number) => void;
}

// Add this interface at the top of the file
interface SelectionItem {
  type: "edge" | "face";
  objectName: string;
  index: number;
  mesh: THREE.Mesh;
  faceIndex?: number;
  edgeGroup?: number[];
}

const HeroView: React.FC<HeroViewProps> = ({
  theme,
  onNavigateToSignUp,
  onSelectionChange,
  onMeasurementChange,
}) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene>();
  const rendererRef = useRef<THREE.WebGLRenderer>();
  const cameraRef = useRef<THREE.PerspectiveCamera>();
  const frameRef = useRef<number>();
  const meshRef = useRef<THREE.Mesh>();
  const naviCubeRef = useRef<HTMLDivElement>(null);
  const lastInteractionRef = useRef<number>(0);
  const AUTO_ROTATE_SPEED = 0.1; // Adjust for faster/slower spin
  const IDLE_TIME_THRESHOLD = 15000; // 15 seconds before auto-rotate starts

  const [cameraRotation, setCameraRotation] = useState({ x: -30, y: -45 });
  const [isHovering, setIsHovering] = useState(false);
  const [hoveredElements, setHoveredElements] = useState<string[]>([]);

  const [selectedItems, setSelectedItems] = useState<SelectionItem[]>([]);
  const [measureDistance, setMeasureDistance] = useState<number>(0);

  // Pass selection state to parent
  useEffect(() => {
    if (onSelectionChange) {
      const simpleSelection = selectedItems.map((item) => ({
        type: item.type,
        index: item.index,
      }));
      onSelectionChange(simpleSelection);
    }
  }, [selectedItems, onSelectionChange]);

  // Listen for clear from parent
  useEffect(() => {
    const handleClear = () => {
      setSelectedItems([]);
      setMeasureDistance(0);
      if (onMeasurementChange) onMeasurementChange(0);
    };
    window.addEventListener("clearHeroSelection", handleClear as EventListener);
    return () =>
      window.removeEventListener(
        "clearHeroSelection",
        handleClear as EventListener,
      );
  }, [onMeasurementChange]);

  // Measurement function
  const calculateEdgeLength = useCallback(
    (mesh: THREE.Mesh, edgeIndex: number): number => {
      const edges = new THREE.EdgesGeometry(mesh.geometry);
      const positions = edges.attributes.position.array;

      const start = new THREE.Vector3(
        positions[edgeIndex * 6],
        positions[edgeIndex * 6 + 1],
        positions[edgeIndex * 6 + 2],
      );
      const end = new THREE.Vector3(
        positions[edgeIndex * 6 + 3],
        positions[edgeIndex * 6 + 4],
        positions[edgeIndex * 6 + 5],
      );

      return start.distanceTo(end);
    },
    [],
  );

  const hoverVisualRef = useRef<THREE.Object3D | null>(null);
  const selectedVisualsRef = useRef<THREE.Object3D[]>([]);

  const exportIcon = theme === "dark" ? exportIconDark : exportIconLight;
  const shareIcon = theme === "dark" ? shareIconDark : shareIconLight;

  const cameraControlsRef = useRef({
    isRotating: false,
    lastMousePosition: { x: 0, y: 0 },
    mouseDownPosition: { x: 0, y: 0 },
    currentDistance: 100,
    rotationVelocity: { x: 0, y: 0 },
    inertia: 0.95,
  });

  const currentRotationRef = useRef({ x: -30, y: -45 });
  const animationRef = useRef({
    isAnimating: false,
    startRotation: { x: 0, y: 0 },
    targetRotation: { x: 0, y: 0 },
    startTime: 0,
    duration: 300,
  });

  const stlLoader = useRef({
    loadSTLFromBase64: function (base64Data: string): THREE.BufferGeometry {
      console.log("🔍 loadSTLFromBase64 called");
      console.log("📊 Base64 data length:", base64Data.length);
      console.log("📊 First 50 chars:", base64Data.substring(0, 50));

      try {
        const binaryString = atob(base64Data);
        console.log("✅ atob successful, binary length:", binaryString.length);

        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        console.log("✅ Bytes array created, length:", bytes.length);

        const header = new TextDecoder().decode(bytes.slice(0, 80));
        console.log("📄 STL Header (first 80 bytes):", header);

        if (header.trim().startsWith("solid")) {
          console.log("🔤 Detected ASCII STL");
          return this.parseASCIISTL(binaryString);
        } else {
          console.log("🔢 Detected Binary STL");
          const dataView = new DataView(bytes.buffer);
          const triangleCount = dataView.getUint32(80, true);
          console.log("📐 Triangle count:", triangleCount);
          return this.parseBinarySTL(bytes);
        }
      } catch (error) {
        console.error("❌ STL parsing error:", error);
        console.error(
          "❌ Error details:",
          error instanceof Error ? error.message : String(error),
        );
        return new THREE.BoxGeometry(20, 15, 10);
      }
    },

    parseASCIISTL: function (stlText: string): THREE.BufferGeometry {
      const vertices: number[] = [];
      const normals: number[] = [];
      const lines = stlText.split("\n");
      let currentNormal = [0, 0, 0];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.startsWith("facet normal")) {
          const parts = line.split(/\s+/);
          currentNormal = [
            parseFloat(parts[2]),
            parseFloat(parts[3]),
            parseFloat(parts[4]),
          ];
        } else if (line.startsWith("vertex")) {
          const parts = line.split(/\s+/);
          vertices.push(
            parseFloat(parts[1]),
            parseFloat(parts[2]),
            parseFloat(parts[3]),
          );
          normals.push(currentNormal[0], currentNormal[1], currentNormal[2]);
        }
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(vertices, 3),
      );
      geometry.setAttribute(
        "normal",
        new THREE.Float32BufferAttribute(normals, 3),
      );
      geometry.computeVertexNormals();
      return geometry;
    },

    parseBinarySTL: function (bytes: Uint8Array): THREE.BufferGeometry {
      console.log("🔢 parseBinarySTL called, bytes.length:", bytes.length);

      const dataView = new DataView(
        bytes.buffer,
        bytes.byteOffset,
        bytes.byteLength,
      );
      const triangleCount = dataView.getUint32(80, true);
      console.log("📐 Triangle count from header:", triangleCount);

      const vertices: number[] = [];
      const normals: number[] = [];

      for (let i = 0; i < triangleCount; i++) {
        const offset = 84 + i * 50;

        // Read normal
        const nx = dataView.getFloat32(offset, true);
        const ny = dataView.getFloat32(offset + 4, true);
        const nz = dataView.getFloat32(offset + 8, true);

        // Read 3 vertices
        for (let j = 0; j < 3; j++) {
          const vertexOffset = offset + 12 + j * 12;
          const x = dataView.getFloat32(vertexOffset + 0, true);
          const y = dataView.getFloat32(vertexOffset + 4, true);
          const z = dataView.getFloat32(vertexOffset + 8, true);
          vertices.push(x, y, z);
          normals.push(nx, ny, nz);
        }
      }

      console.log("📊 Parsed vertices count:", vertices.length / 3);
      console.log("📊 First few vertices:", vertices.slice(0, 9));

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(vertices, 3),
      );
      geometry.setAttribute(
        "normal",
        new THREE.Float32BufferAttribute(normals, 3),
      );
      geometry.computeVertexNormals();

      console.log("✅ Geometry attributes set");
      console.log(
        "📊 Position attribute count:",
        geometry.attributes.position.count,
      );

      return geometry;
    },
  }).current;

  const normalizeAngle = useCallback((angle: number) => {
    while (angle > 180) angle -= 360;
    while (angle < -180) angle += 360;
    return angle;
  }, []);

  const calculateShortestPath = useCallback(
    (from: { x: number; y: number }, to: { x: number; y: number }) => {
      const deltaX = to.x - from.x;
      let deltaY = to.y - from.y;

      while (deltaY > 180) deltaY -= 360;
      while (deltaY < -180) deltaY += 360;

      return {
        x: from.x + deltaX,
        y: from.y + deltaY,
      };
    },
    [],
  );

  const interpolateRotation = useCallback(
    (
      start: { x: number; y: number },
      target: { x: number; y: number },
      progress: number,
    ) => {
      let deltaY = target.y - start.y;
      while (deltaY > 180) deltaY -= 360;
      while (deltaY < -180) deltaY += 360;

      const deltaX = target.x - start.x;
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      return {
        x: start.x + deltaX * easeProgress,
        y: start.y + deltaY * easeProgress,
      };
    },
    [],
  );

  const updateCameraPosition = useCallback(
    (rotation: { x: number; y: number }, distance?: number) => {
      const currentDistance =
        distance || cameraControlsRef.current.currentDistance;
      const spherical = new THREE.Spherical();

      spherical.radius = currentDistance;
      spherical.phi = THREE.MathUtils.degToRad(90 + rotation.x);
      spherical.theta = THREE.MathUtils.degToRad(-rotation.y);

      const normalizedX = ((rotation.x % 360) + 360) % 360;
      const shouldFlipUp = normalizedX > 90 && normalizedX < 270;
      const upVector = shouldFlipUp
        ? new THREE.Vector3(0, -1, 0)
        : new THREE.Vector3(0, 1, 0);

      if (cameraRef.current) {
        cameraRef.current.position.setFromSpherical(spherical);
        cameraRef.current.up.copy(upVector);
        cameraRef.current.lookAt(0, 0, 0);
      }

      currentRotationRef.current = rotation;
    },
    [],
  );

  const updateNaviCube = useCallback((rotation: { x: number; y: number }) => {
    if (naviCubeRef.current) {
      const isAnimating = animationRef.current.isAnimating;
      if (isAnimating) {
        naviCubeRef.current.style.transition = "none";
      } else {
        naviCubeRef.current.style.transition =
          "transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
      }

      naviCubeRef.current.style.transform = `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`;
    }
  }, []);

  const animateCameraToRotation = useCallback(
    (targetRotation: { x: number; y: number }) => {
      const animation = animationRef.current;
      animation.isAnimating = true;
      animation.startRotation = { ...currentRotationRef.current };
      animation.targetRotation = { ...targetRotation };
      animation.startTime = performance.now();

      cameraControlsRef.current.rotationVelocity = { x: 0, y: 0 };
    },
    [],
  );

  const handleFaceClick = (face: string, region: string) => {
    lastInteractionRef.current = Date.now(); // ADD THIS LINE
    const rotations = {
      "front-center": { x: 0, y: 0 },
      "back-center": { x: 0, y: 180 },
      "right-center": { x: 0, y: -90 },
      "left-center": { x: 0, y: 90 },
      "top-center": { x: -90, y: 0 },
      "bottom-center": { x: 90, y: 0 },
      "front-top-left": { x: -30, y: 45 },
      "front-top": { x: -45, y: 0 },
      "front-top-right": { x: -30, y: -45 },
      "front-left": { x: 0, y: 45 },
      "front-right": { x: 0, y: -45 },
      "front-bottom-left": { x: 30, y: 45 },
      "front-bottom": { x: 45, y: 0 },
      "front-bottom-right": { x: 30, y: -45 },
      "right-top-left": { x: -30, y: -45 },
      "right-top": { x: -45, y: -90 },
      "right-top-right": { x: -30, y: -135 },
      "right-left": { x: 0, y: -45 },
      "right-right": { x: 0, y: -135 },
      "right-bottom-left": { x: 30, y: -45 },
      "right-bottom": { x: 45, y: -90 },
      "right-bottom-right": { x: 30, y: -135 },
      "left-top-left": { x: -30, y: 135 },
      "left-top": { x: -45, y: 90 },
      "left-top-right": { x: -30, y: 45 },
      "left-left": { x: 0, y: 135 },
      "left-right": { x: 0, y: 45 },
      "left-bottom-left": { x: 30, y: 135 },
      "left-bottom": { x: 45, y: 90 },
      "left-bottom-right": { x: 30, y: 45 },
      "back-top-left": { x: -30, y: -135 },
      "back-top": { x: -45, y: 180 },
      "back-top-right": { x: -30, y: 135 },
      "back-left": { x: 0, y: -135 },
      "back-right": { x: 0, y: 135 },
      "back-bottom-left": { x: 30, y: -135 },
      "back-bottom": { x: 45, y: 180 },
      "back-bottom-right": { x: 30, y: 135 },
      "top-top-left": { x: -30, y: 135 },
      "top-top": { x: -45, y: 180 },
      "top-top-right": { x: -30, y: -135 },
      "top-left": { x: -45, y: 90 },
      "top-right": { x: -45, y: -90 },
      "top-bottom-left": { x: -30, y: 45 },
      "top-bottom": { x: -45, y: 0 },
      "top-bottom-right": { x: -30, y: -45 },
      "bottom-top-left": { x: 30, y: 45 },
      "bottom-top": { x: 45, y: 0 },
      "bottom-top-right": { x: 30, y: -45 },
      "bottom-left": { x: 45, y: 90 },
      "bottom-right": { x: 45, y: -90 },
      "bottom-bottom-left": { x: 30, y: 135 },
      "bottom-bottom": { x: 45, y: 180 },
      "bottom-bottom-right": { x: 30, y: -135 },
    };

    const key = `${face}-${region}`;
    if (rotations[key as keyof typeof rotations]) {
      const targetRotation = rotations[key as keyof typeof rotations];
      const normalizedCurrent = {
        x: currentRotationRef.current.x,
        y: normalizeAngle(currentRotationRef.current.y),
      };
      const shortestTarget = calculateShortestPath(
        normalizedCurrent,
        targetRotation,
      );
      animateCameraToRotation(shortestTarget);
    }
  };

  const getCornerHoverElements = (corner: string) => {
    const cornerMappings = {
      "front-top-left": ["front-top-left", "left-top-right", "top-bottom-left"],
      "front-top-right": [
        "front-top-right",
        "right-top-left",
        "top-bottom-right",
      ],
      "front-bottom-left": [
        "front-bottom-left",
        "left-bottom-right",
        "bottom-top-left",
      ],
      "front-bottom-right": [
        "front-bottom-right",
        "right-bottom-left",
        "bottom-top-right",
      ],
      "right-top-left": [
        "right-top-left",
        "front-top-right",
        "top-bottom-right",
      ],
      "right-top-right": ["right-top-right", "back-top-left", "top-top-right"],
      "right-bottom-left": [
        "right-bottom-left",
        "front-bottom-right",
        "bottom-top-right",
      ],
      "right-bottom-right": [
        "right-bottom-right",
        "back-bottom-left",
        "bottom-bottom-right",
      ],
      "left-top-left": ["left-top-left", "back-top-right", "top-top-left"],
      "left-top-right": ["left-top-right", "front-top-left", "top-bottom-left"],
      "left-bottom-left": [
        "left-bottom-left",
        "back-bottom-right",
        "bottom-bottom-left",
      ],
      "left-bottom-right": [
        "left-bottom-right",
        "front-bottom-left",
        "bottom-top-left",
      ],
      "back-top-left": ["back-top-left", "right-top-right", "top-top-right"],
      "back-top-right": ["back-top-right", "left-top-left", "top-top-left"],
      "back-bottom-left": [
        "back-bottom-left",
        "right-bottom-right",
        "bottom-bottom-right",
      ],
      "back-bottom-right": [
        "back-bottom-right",
        "left-bottom-left",
        "bottom-bottom-left",
      ],
      "top-top-left": ["top-top-left", "back-top-right", "left-top-left"],
      "top-top-right": ["top-top-right", "back-top-left", "right-top-right"],
      "top-bottom-left": [
        "top-bottom-left",
        "front-top-left",
        "left-top-right",
      ],
      "top-bottom-right": [
        "top-bottom-right",
        "front-top-right",
        "right-top-left",
      ],
      "bottom-top-left": [
        "bottom-top-left",
        "front-bottom-left",
        "left-bottom-right",
      ],
      "bottom-top-right": [
        "bottom-top-right",
        "front-bottom-right",
        "right-bottom-left",
      ],
      "bottom-bottom-left": [
        "bottom-bottom-left",
        "back-bottom-right",
        "left-bottom-left",
      ],
      "bottom-bottom-right": [
        "bottom-bottom-right",
        "back-bottom-left",
        "right-bottom-right",
      ],
    };
    return cornerMappings[corner as keyof typeof cornerMappings] || [];
  };

  const getEdgeHoverElements = (edge: string) => {
    const edgeMappings = {
      "front-top": ["front-top", "top-bottom"],
      "front-bottom": ["front-bottom", "bottom-top"],
      "front-left": ["front-left", "left-right"],
      "front-right": ["front-right", "right-left"],
      "right-top": ["right-top", "top-right"],
      "right-bottom": ["right-bottom", "bottom-right"],
      "right-left": ["right-left", "front-right"],
      "right-right": ["right-right", "back-left"],
      "left-top": ["left-top", "top-left"],
      "left-bottom": ["left-bottom", "bottom-left"],
      "left-left": ["left-left", "back-right"],
      "left-right": ["left-right", "front-left"],
      "back-top": ["back-top", "top-top"],
      "back-bottom": ["back-bottom", "bottom-bottom"],
      "back-left": ["back-left", "right-right"],
      "back-right": ["back-right", "left-left"],
      "top-top": ["top-top", "back-top"],
      "top-bottom": ["top-bottom", "front-top"],
      "top-left": ["top-left", "left-top"],
      "top-right": ["top-right", "right-top"],
      "bottom-top": ["bottom-top", "front-bottom"],
      "bottom-bottom": ["bottom-bottom", "back-bottom"],
      "bottom-left": ["bottom-left", "left-bottom"],
      "bottom-right": ["bottom-right", "right-bottom"],
    };
    return edgeMappings[edge as keyof typeof edgeMappings] || [];
  };

  const handleHover = (face: string, region: string, isEntering: boolean) => {
    const elementKey = `${face}-${region}`;
    let elementsToHighlight: string[] = [];

    if (region === "center") {
      elementsToHighlight = [
        `${face}-center`,
        `${face}-top-left`,
        `${face}-top`,
        `${face}-top-right`,
        `${face}-left`,
        `${face}-right`,
        `${face}-bottom-left`,
        `${face}-bottom`,
        `${face}-bottom-right`,
      ];
    } else if (region.includes("-")) {
      elementsToHighlight = getCornerHoverElements(elementKey);
    } else {
      elementsToHighlight = getEdgeHoverElements(elementKey);
    }

    if (isEntering) {
      setHoveredElements(elementsToHighlight);
    } else {
      setHoveredElements([]);
    }
  };

  const renderFace = (faceName: string, transformClass: string) => (
    <div className={`${styles.face} ${styles[transformClass]}`}>
      <div className={styles.faceGrid}>
        <div
          className={`${styles.faceRegion} ${styles.cornerRegion} ${
            hoveredElements.includes(`${faceName}-top-left`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "top-left")}
          onMouseEnter={() => handleHover(faceName, "top-left", true)}
          onMouseLeave={() => handleHover(faceName, "top-left", false)}
        />
        <div
          className={`${styles.faceRegion} ${styles.edgeRegion} ${
            hoveredElements.includes(`${faceName}-top`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "top")}
          onMouseEnter={() => handleHover(faceName, "top", true)}
          onMouseLeave={() => handleHover(faceName, "top", false)}
        />
        <div
          className={`${styles.faceRegion} ${styles.cornerRegion} ${
            hoveredElements.includes(`${faceName}-top-right`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "top-right")}
          onMouseEnter={() => handleHover(faceName, "top-right", true)}
          onMouseLeave={() => handleHover(faceName, "top-right", false)}
        />
        <div
          className={`${styles.faceRegion} ${styles.edgeRegion} ${
            hoveredElements.includes(`${faceName}-left`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "left")}
          onMouseEnter={() => handleHover(faceName, "left", true)}
          onMouseLeave={() => handleHover(faceName, "left", false)}
        />
        <div
          className={`${styles.faceRegion} ${styles.centerRegion} ${
            hoveredElements.includes(`${faceName}-center`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "center")}
          onMouseEnter={() => handleHover(faceName, "center", true)}
          onMouseLeave={() => handleHover(faceName, "center", false)}
        >
          {faceName.toUpperCase()}
        </div>
        <div
          className={`${styles.faceRegion} ${styles.edgeRegion} ${
            hoveredElements.includes(`${faceName}-right`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "right")}
          onMouseEnter={() => handleHover(faceName, "right", true)}
          onMouseLeave={() => handleHover(faceName, "right", false)}
        />
        <div
          className={`${styles.faceRegion} ${styles.cornerRegion} ${
            hoveredElements.includes(`${faceName}-bottom-left`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "bottom-left")}
          onMouseEnter={() => handleHover(faceName, "bottom-left", true)}
          onMouseLeave={() => handleHover(faceName, "bottom-left", false)}
        />
        <div
          className={`${styles.faceRegion} ${styles.edgeRegion} ${
            hoveredElements.includes(`${faceName}-bottom`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "bottom")}
          onMouseEnter={() => handleHover(faceName, "bottom", true)}
          onMouseLeave={() => handleHover(faceName, "bottom", false)}
        />
        <div
          className={`${styles.faceRegion} ${styles.cornerRegion} ${
            hoveredElements.includes(`${faceName}-bottom-right`)
              ? styles.highlighted
              : ""
          }`}
          onClick={() => handleFaceClick(faceName, "bottom-right")}
          onMouseEnter={() => handleHover(faceName, "bottom-right", true)}
          onMouseLeave={() => handleHover(faceName, "bottom-right", false)}
        />
      </div>
    </div>
  );

  // Initialize Three.js scene
  useEffect(() => {
    if (!mountRef.current) return;

    const scene = new THREE.Scene();
    const bgColor = getComputedStyle(document.documentElement)
      .getPropertyValue("--color-background")
      .trim();
    scene.background = new THREE.Color(bgColor || "#f4f4f4");
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      50,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      50000,
    );
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(
      mountRef.current.clientWidth,
      mountRef.current.clientHeight,
    );
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    rendererRef.current = renderer;

    // Lighting setup
    // Replace the entire "Lighting setup" block in HeroView.tsx with this:

    const ambientLight = new THREE.AmbientLight(0xf8f9fa, 0.3);
    scene.add(ambientLight);

    const keyLight = new THREE.DirectionalLight(0xffffff, 1.2);
    keyLight.position.set(100, 120, 80);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.width = 4096;
    keyLight.shadow.mapSize.height = 4096;
    keyLight.shadow.camera.near = 0.1;
    keyLight.shadow.camera.far = 1000;
    keyLight.shadow.camera.left = -200;
    keyLight.shadow.camera.right = 200;
    keyLight.shadow.camera.top = 200;
    keyLight.shadow.camera.bottom = -200;
    keyLight.shadow.bias = -0.0005;
    keyLight.shadow.normalBias = 0.02;
    keyLight.shadow.radius = 4;
    keyLight.userData = { originalIntensity: 1.2, type: "key" };
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xe8f4f8, 0.6);
    fillLight.position.set(-80, 60, 40);
    fillLight.castShadow = true;
    fillLight.shadow.mapSize.width = 2048;
    fillLight.shadow.mapSize.height = 2048;
    fillLight.shadow.camera.near = 0.1;
    fillLight.shadow.camera.far = 800;
    fillLight.shadow.camera.left = -150;
    fillLight.shadow.camera.right = 150;
    fillLight.shadow.camera.top = 150;
    fillLight.shadow.camera.bottom = -150;
    fillLight.shadow.bias = -0.0003;
    fillLight.shadow.radius = 6;
    fillLight.userData = { originalIntensity: 0.6, type: "fill" };
    scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0xfff8e1, 0.8);
    rimLight.position.set(-60, 40, -100);
    rimLight.userData = { originalIntensity: 0.8, type: "rim" };
    scene.add(rimLight);

    const topLight = new THREE.DirectionalLight(0xf0f8ff, 0.5);
    topLight.position.set(0, 150, 0);
    topLight.userData = { originalIntensity: 0.5, type: "top" };
    scene.add(topLight);

    const hemisphereLight = new THREE.HemisphereLight(0xb8d4f0, 0x8b7355, 0.4);
    hemisphereLight.position.set(0, 100, 0);
    hemisphereLight.userData = { originalIntensity: 0.4, type: "hemisphere" };
    scene.add(hemisphereLight);

    mountRef.current.appendChild(renderer.domElement);

    // Load static geometry
    // Load STL file directly
    // Load STL file directly
    const loadSTLModel = async () => {
      try {
        console.log("📦 Starting STL load from /assets/hero-model.stl");

        const response = await fetch(heroModelUrl);

        console.log("📡 Fetch response:", response.status, response.statusText);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        console.log(
          "📦 ArrayBuffer received, size:",
          arrayBuffer.byteLength,
          "bytes",
        );

        const bytes = new Uint8Array(arrayBuffer);
        console.log("📦 Uint8Array created, length:", bytes.length);

        let geometry: THREE.BufferGeometry;

        // Better detection: check if file starts with "solid" as text
        const text = new TextDecoder().decode(bytes);
        const firstLine = text.substring(0, 100).trim();

        console.log("📄 First 100 chars:", firstLine);

        // ASCII STL files start with "solid" followed by optional name
        // Binary STL can have "solid" in the header but won't be valid text
        const isASCII = firstLine.startsWith("solid") && text.includes("facet");

        if (isASCII) {
          console.log("🔤 Parsing as ASCII STL");
          console.log("📝 Text length:", text.length);
          geometry = stlLoader.parseASCIISTL(text);
        } else {
          console.log("🔢 Parsing as Binary STL");
          const dataView = new DataView(
            bytes.buffer,
            bytes.byteOffset,
            bytes.byteLength,
          );
          const triangleCount = dataView.getUint32(80, true);
          console.log("📐 Binary STL triangle count:", triangleCount);
          geometry = stlLoader.parseBinarySTL(bytes);
        }

        console.log("✅ Geometry created");
        console.log(
          "📊 Has position attribute?",
          !!geometry.attributes.position,
        );
        if (geometry.attributes.position) {
          console.log("📊 Position count:", geometry.attributes.position.count);
        }

        const color = new THREE.Color(theme === "dark" ? "#6f6f6f" : "#8d8d8d");
        const material = new THREE.MeshStandardMaterial({
          color: color,
          metalness: 0.1,
          roughness: 0.2,
        });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.rotation.x = -Math.PI / 2;
        mesh.updateMatrixWorld();

        const box = new THREE.Box3().setFromObject(mesh);
        const center = new THREE.Vector3();
        box.getCenter(center);
        mesh.position.sub(center);

        mesh.castShadow = true;
        mesh.receiveShadow = true;
        mesh.userData = { isNoahShape: true };
        scene.add(mesh);
        meshRef.current = mesh;

        // Auto-fit camera
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const distance = maxDim * 2.5;

        cameraControlsRef.current.currentDistance = distance;
        updateCameraPosition({ x: -30, y: -45 }, distance);
      } catch (error) {
        console.error("❌ Failed to load STL:", error);

        // Fallback to a simple box
        const geometry = new THREE.BoxGeometry(20, 15, 10);
        const material = new THREE.MeshStandardMaterial({
          color: new THREE.Color(theme === "dark" ? "#6f6f6f" : "#8d8d8d"),
          metalness: 0.1,
          roughness: 0.2,
        });
        const mesh = new THREE.Mesh(geometry, material);
        scene.add(mesh);
        meshRef.current = mesh;
      }
    };

    // Call the async function
    loadSTLModel();

    // Animation loop with smooth transitions
    // Animation loop with smooth transitions and auto-rotation
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);

      const controls = cameraControlsRef.current;
      const animation = animationRef.current;

      // Handle smooth NaviCube click animations
      if (animation.isAnimating) {
        const elapsed = performance.now() - animation.startTime;
        const progress = Math.min(elapsed / animation.duration, 1);

        const currentRotation = interpolateRotation(
          animation.startRotation,
          animation.targetRotation,
          progress,
        );

        updateCameraPosition(currentRotation, controls.currentDistance);
        updateNaviCube(currentRotation);
        setCameraRotation(currentRotation);

        if (progress >= 1) {
          animation.isAnimating = false;
        }
      }
      // Handle manual rotation inertia
      else if (
        !controls.isRotating &&
        (Math.abs(controls.rotationVelocity.x) > 0.01 ||
          Math.abs(controls.rotationVelocity.y) > 0.01)
      ) {
        const newRotation = {
          x: currentRotationRef.current.x + controls.rotationVelocity.x,
          y: currentRotationRef.current.y + controls.rotationVelocity.y,
        };

        updateCameraPosition(newRotation, controls.currentDistance);
        updateNaviCube(newRotation);

        controls.rotationVelocity.x *= controls.inertia;
        controls.rotationVelocity.y *= controls.inertia;

        if (Math.abs(controls.rotationVelocity.x) < 0.01)
          controls.rotationVelocity.x = 0;
        if (Math.abs(controls.rotationVelocity.y) < 0.01)
          controls.rotationVelocity.y = 0;
      }
      // AUTO-ROTATION: Start spinning if idle for 15 seconds
      else if (
        !controls.isRotating &&
        !animation.isAnimating &&
        Date.now() - lastInteractionRef.current > IDLE_TIME_THRESHOLD
      ) {
        const newRotation = {
          x: currentRotationRef.current.x,
          y: currentRotationRef.current.y - AUTO_ROTATE_SPEED,
        };

        updateCameraPosition(newRotation, controls.currentDistance);
        updateNaviCube(newRotation);
      }

      if (cameraRef.current && sceneRef.current) {
        renderer.render(sceneRef.current, cameraRef.current);
      }
    };
    animate();

    lastInteractionRef.current = 0; // ADD THIS LINE to trigger immediate auto-rotation

    // Handle resize
    const handleResize = () => {
      if (!mountRef.current || !cameraRef.current || !renderer) return;

      const width = mountRef.current.clientWidth;
      const height = mountRef.current.clientHeight;

      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
      renderer.setSize(width, height);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
      scene.clear();
    };
  }, [theme, updateCameraPosition, updateNaviCube, interpolateRotation]);

  // Create edge visual highlight
  const createEdgeVisual = useCallback(
    (
      mesh: THREE.Mesh,
      edgeIndex: number,
      color?: number,
      linewidth: number = 6,
    ) => {
      const edges = new THREE.EdgesGeometry(mesh.geometry);
      const positions = edges.attributes.position.array;

      const singleEdgePositions = [
        positions[edgeIndex * 6],
        positions[edgeIndex * 6 + 1],
        positions[edgeIndex * 6 + 2],
        positions[edgeIndex * 6 + 3],
        positions[edgeIndex * 6 + 4],
        positions[edgeIndex * 6 + 5],
      ];

      const singleEdgeGeometry = new THREE.BufferGeometry();
      singleEdgeGeometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(singleEdgePositions, 3),
      );

      const edgeColor = color !== undefined ? color : 0xffffff;

      const edgeMaterial = new THREE.LineBasicMaterial({
        color: edgeColor,
        linewidth: linewidth,
        transparent: true,
        opacity: 1.0,
      });
      const edgeLine = new THREE.LineSegments(singleEdgeGeometry, edgeMaterial);

      edgeLine.position.copy(mesh.position);
      edgeLine.rotation.copy(mesh.rotation);
      edgeLine.scale.copy(mesh.scale);
      edgeLine.userData = { isSelectionVisual: true };

      return edgeLine;
    },
    [],
  );

  // Create face visual highlight
  const createFaceVisual = useCallback(
    (
      mesh: THREE.Mesh,
      faceIndex: number,
      color?: number,
      opacity: number = 0.3,
    ) => {
      const geometry = mesh.geometry;
      const positions = geometry.attributes.position.array;
      const normals = geometry.attributes.normal?.array;

      if (!normals) return null;

      const triangleStart = faceIndex * 9;
      const clickedNormal = new THREE.Vector3(
        normals[triangleStart],
        normals[triangleStart + 1],
        normals[triangleStart + 2],
      );

      // Find connected triangles with same normal
      const connectedTriangles = new Set<number>();
      const toProcess = [faceIndex];
      connectedTriangles.add(faceIndex);
      const tolerance = 0.0001;

      while (toProcess.length > 0) {
        const currentTriIndex = toProcess.pop()!;
        const currentStart = currentTriIndex * 9;
        const currentVertices = [
          new THREE.Vector3(
            positions[currentStart],
            positions[currentStart + 1],
            positions[currentStart + 2],
          ),
          new THREE.Vector3(
            positions[currentStart + 3],
            positions[currentStart + 4],
            positions[currentStart + 5],
          ),
          new THREE.Vector3(
            positions[currentStart + 6],
            positions[currentStart + 7],
            positions[currentStart + 8],
          ),
        ];

        for (let i = 0; i < positions.length; i += 9) {
          const triIndex = i / 9;
          if (connectedTriangles.has(triIndex)) continue;

          const triangleNormal = new THREE.Vector3(
            normals[i],
            normals[i + 1],
            normals[i + 2],
          );
          if (clickedNormal.distanceTo(triangleNormal) >= tolerance) continue;

          const otherVertices = [
            new THREE.Vector3(positions[i], positions[i + 1], positions[i + 2]),
            new THREE.Vector3(
              positions[i + 3],
              positions[i + 4],
              positions[i + 5],
            ),
            new THREE.Vector3(
              positions[i + 6],
              positions[i + 7],
              positions[i + 8],
            ),
          ];

          let sharedVertices = 0;
          for (const currentVertex of currentVertices) {
            for (const otherVertex of otherVertices) {
              if (currentVertex.distanceTo(otherVertex) < 0.001) {
                sharedVertices++;
                break;
              }
            }
          }

          if (sharedVertices >= 2) {
            connectedTriangles.add(triIndex);
            toProcess.push(triIndex);
          }
        }
      }

      // Build geometry from connected triangles
      const sameFaceVertices: number[] = [];
      for (const triIndex of connectedTriangles) {
        const i = triIndex * 9;
        sameFaceVertices.push(
          positions[i],
          positions[i + 1],
          positions[i + 2],
          positions[i + 3],
          positions[i + 4],
          positions[i + 5],
          positions[i + 6],
          positions[i + 7],
          positions[i + 8],
        );
      }

      if (sameFaceVertices.length === 0) return null;

      const faceGeometry = new THREE.BufferGeometry();
      faceGeometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(sameFaceVertices, 3),
      );

      const indices = [];
      for (let i = 0; i < sameFaceVertices.length / 3; i += 3) {
        indices.push(i, i + 1, i + 2);
      }
      faceGeometry.setIndex(indices);

      const faceColor = color !== undefined ? color : 0xffffff;

      const faceMaterial = new THREE.MeshBasicMaterial({
        color: faceColor,
        transparent: true,
        opacity,
        side: THREE.DoubleSide,
      });

      const faceMesh = new THREE.Mesh(faceGeometry, faceMaterial);
      faceMesh.position.copy(mesh.position);
      faceMesh.rotation.copy(mesh.rotation);
      faceMesh.scale.copy(mesh.scale);
      faceMesh.userData = { isSelectionVisual: true };

      return faceMesh;
    },
    [],
  );

  // Clear hover visual
  const clearHoverVisual = useCallback(() => {
    if (hoverVisualRef.current && sceneRef.current) {
      sceneRef.current.remove(hoverVisualRef.current);
      hoverVisualRef.current = null;
    }
  }, []);

  // Clear selected visuals
  const clearSelectedVisuals = useCallback(() => {
    if (sceneRef.current) {
      selectedVisualsRef.current.forEach((visual) => {
        sceneRef.current!.remove(visual);
      });
      selectedVisualsRef.current = [];
    }
  }, []);

  // Update selection visuals when selection changes
  const updateSelectionVisuals = useCallback(() => {
    clearSelectedVisuals();

    selectedItems.forEach((item) => {
      let visual: THREE.Object3D | null = null;

      if (item.type === "edge") {
        visual = createEdgeVisual(item.mesh, item.index, 0xffffff, 8);
      } else if (item.type === "face") {
        visual = createFaceVisual(item.mesh, item.index, 0xffffff, 0.5);
      }

      if (visual && sceneRef.current) {
        sceneRef.current.add(visual);
        selectedVisualsRef.current.push(visual);
      }
    });
  }, [selectedItems, createEdgeVisual, createFaceVisual, clearSelectedVisuals]);

  // Update visuals when selection changes
  useEffect(() => {
    updateSelectionVisuals();
  }, [selectedItems, updateSelectionVisuals]);

  // Mouse interaction
  // Mouse interaction with selection
  useEffect(() => {
    if (!mountRef.current) return;

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    // ── TOUCH STATE ──────────────────────────────────────────────────────────
    const touchStateRef_local = {
      isTouching: false,
      lastTouchPosition: { x: 0, y: 0 },
      touchStartPosition: { x: 0, y: 0 },
      isPinching: false,
      lastPinchDistance: 0,
      hasMoved: false,
      touchStartTime: 0,
    };

    // ── MOUSE HANDLERS ───────────────────────────────────────────────────────
    const handleMouseDown = (event: MouseEvent) => {
      lastInteractionRef.current = Date.now();
      const controls = cameraControlsRef.current;
      controls.isRotating = true;
      controls.lastMousePosition = { x: event.clientX, y: event.clientY };
      controls.mouseDownPosition = { x: event.clientX, y: event.clientY };
      controls.rotationVelocity = { x: 0, y: 0 };
      if (mountRef.current) {
        mountRef.current.style.cursor = "grabbing";
      }
    };

    const handleMouseUp = () => {
      cameraControlsRef.current.isRotating = false;
      if (mountRef.current) {
        mountRef.current.style.cursor = isHovering ? "grab" : "grab";
      }
    };

    const handleMouseMove = (event: MouseEvent) => {
      const controls = cameraControlsRef.current;

      if (controls.isRotating) {
        const deltaX = event.clientX - controls.lastMousePosition.x;
        const deltaY = event.clientY - controls.lastMousePosition.y;

        const rotationSpeed = 0.5;
        const deltaRotationX = deltaY * rotationSpeed;
        const deltaRotationY = -deltaX * rotationSpeed;

        controls.rotationVelocity.x = deltaRotationX * 0.1;
        controls.rotationVelocity.y = deltaRotationY * 0.1;

        const newRotation = {
          x: currentRotationRef.current.x + deltaRotationX,
          y: currentRotationRef.current.y + deltaRotationY,
        };

        animationRef.current.isAnimating = false;
        updateCameraPosition(newRotation, controls.currentDistance);
        updateNaviCube(newRotation);
        setCameraRotation(newRotation);

        controls.lastMousePosition = { x: event.clientX, y: event.clientY };
      } else {
        if (!mountRef.current) return;

        const rect = mountRef.current.getBoundingClientRect();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        if (cameraRef.current) {
          raycaster.setFromCamera(mouse, cameraRef.current);
        }

        const noahMeshes = sceneRef.current!.children.filter(
          (child) => child instanceof THREE.Mesh && child.userData?.isNoahShape,
        );
        const intersects = raycaster.intersectObjects(noahMeshes);

        clearHoverVisual();

        if (intersects.length > 0) {
          const intersectedMesh = intersects[0].object as THREE.Mesh;
          const intersectionPoint = intersects[0].point;
          const face = intersects[0].face;

          const edgeThreshold = 0.5;
          let closestEdgeIndex = 0;
          let minEdgeDistance = Infinity;

          const edges = new THREE.EdgesGeometry(intersectedMesh.geometry);
          const positions = edges.attributes.position.array;

          for (let i = 0; i < positions.length; i += 6) {
            const start = new THREE.Vector3(
              positions[i],
              positions[i + 1],
              positions[i + 2],
            );
            const end = new THREE.Vector3(
              positions[i + 3],
              positions[i + 4],
              positions[i + 5],
            );

            start.applyMatrix4(intersectedMesh.matrixWorld);
            end.applyMatrix4(intersectedMesh.matrixWorld);

            const line = new THREE.Line3(start, end);
            const closestPoint = new THREE.Vector3();
            line.closestPointToPoint(intersectionPoint, true, closestPoint);
            const distance = intersectionPoint.distanceTo(closestPoint);

            if (distance < minEdgeDistance) {
              minEdgeDistance = distance;
              closestEdgeIndex = i / 6;
            }
          }

          const isHoveringEdge = minEdgeDistance < edgeThreshold;

          if (isHoveringEdge) {
            hoverVisualRef.current = createEdgeVisual(
              intersectedMesh,
              Math.floor(closestEdgeIndex),
              0xffffff,
              6,
            );
            if (hoverVisualRef.current) {
              sceneRef.current!.add(hoverVisualRef.current);
            }
            mountRef.current!.style.cursor = "pointer";
          } else if (face) {
            const faceIndex = Math.floor(intersects[0].faceIndex || 0);
            hoverVisualRef.current = createFaceVisual(
              intersectedMesh,
              faceIndex,
              0xffffff,
              0.3,
            );
            if (hoverVisualRef.current) {
              sceneRef.current!.add(hoverVisualRef.current);
            }
            mountRef.current!.style.cursor = "pointer";
          }
        } else {
          mountRef.current!.style.cursor = "grab";
        }
      }
    };

    const handleClick = (event: MouseEvent) => {
      const controls = cameraControlsRef.current;

      const deltaX = Math.abs(event.clientX - controls.mouseDownPosition.x);
      const deltaY = Math.abs(event.clientY - controls.mouseDownPosition.y);
      const totalMovement = deltaX + deltaY;

      if (totalMovement > 5) {
        return;
      }

      if (!mountRef.current) return;

      const rect = mountRef.current.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      if (cameraRef.current) {
        raycaster.setFromCamera(mouse, cameraRef.current);
      }

      const noahMeshes = sceneRef.current!.children.filter(
        (child) => child instanceof THREE.Mesh && child.userData?.isNoahShape,
      );

      const intersects = raycaster.intersectObjects(noahMeshes);

      if (intersects.length > 0) {
        const intersectedMesh = intersects[0].object as THREE.Mesh;
        const intersectionPoint = intersects[0].point;

        const edgeThreshold = 0.5;
        let closestEdgeIndex = 0;
        let minEdgeDistance = Infinity;

        const edges = new THREE.EdgesGeometry(intersectedMesh.geometry);
        const positions = edges.attributes.position.array;

        for (let i = 0; i < positions.length; i += 6) {
          const start = new THREE.Vector3(
            positions[i],
            positions[i + 1],
            positions[i + 2],
          );
          const end = new THREE.Vector3(
            positions[i + 3],
            positions[i + 4],
            positions[i + 5],
          );

          start.applyMatrix4(intersectedMesh.matrixWorld);
          end.applyMatrix4(intersectedMesh.matrixWorld);

          const line = new THREE.Line3(start, end);
          const closestPoint = new THREE.Vector3();
          line.closestPointToPoint(intersectionPoint, true, closestPoint);
          const distance = intersectionPoint.distanceTo(closestPoint);

          if (distance < minEdgeDistance) {
            minEdgeDistance = distance;
            closestEdgeIndex = i / 6;
          }
        }

        const isEdgeSelection = minEdgeDistance < edgeThreshold;

        if (isEdgeSelection) {
          const actualIndex = Math.floor(closestEdgeIndex);
          const measurement = calculateEdgeLength(intersectedMesh, actualIndex);

          let newSelection: SelectionItem = {
            type: "edge",
            objectName: intersectedMesh.userData.name || "Unknown",
            index: actualIndex,
            mesh: intersectedMesh,
            faceIndex: 0,
          };

          setSelectedItems((prev) => {
            const exists = prev.find(
              (item) =>
                item.objectName === newSelection.objectName &&
                item.type === newSelection.type &&
                item.index === newSelection.index,
            );

            if (exists) {
              const remaining = prev.filter((item) => item !== exists);
              const remainingEdges = remaining.filter((i) => i.type === "edge");
              if (remainingEdges.length === 0) {
                setMeasureDistance(0);
                if (onMeasurementChange) onMeasurementChange(0);
              } else {
                const lastEdge = remainingEdges[remainingEdges.length - 1];
                const recalculated = calculateEdgeLength(
                  lastEdge.mesh,
                  lastEdge.index,
                );
                setMeasureDistance(recalculated);
                if (onMeasurementChange) onMeasurementChange(recalculated);
              }
              return remaining;
            } else {
              setMeasureDistance(measurement);
              if (onMeasurementChange) onMeasurementChange(measurement);
              return [...prev, newSelection];
            }
          });
        } else if (intersects[0].face) {
          const faceIndex = Math.floor(intersects[0].faceIndex || 0);

          let newSelection: SelectionItem = {
            type: "face",
            objectName: intersectedMesh.userData.name || "Unknown",
            index: faceIndex,
            mesh: intersectedMesh,
            faceIndex: faceIndex,
          };

          setSelectedItems((prev) => {
            const exists = prev.find(
              (item) =>
                item.objectName === newSelection.objectName &&
                item.type === newSelection.type &&
                item.index === newSelection.index,
            );

            if (exists) {
              const remaining = prev.filter((item) => item !== exists);
              const remainingEdges = remaining.filter((i) => i.type === "edge");
              if (remainingEdges.length === 0) {
                setMeasureDistance(0);
                if (onMeasurementChange) onMeasurementChange(0);
              } else {
                const lastEdge = remainingEdges[remainingEdges.length - 1];
                const recalculated = calculateEdgeLength(
                  lastEdge.mesh,
                  lastEdge.index,
                );
                setMeasureDistance(recalculated);
                if (onMeasurementChange) onMeasurementChange(recalculated);
              }
              return remaining;
            } else {
              return [...prev, newSelection];
            }
          });
        }
      }
    };

    const handleWheel = (event: WheelEvent) => {
      lastInteractionRef.current = Date.now();
      event.preventDefault();
      const controls = cameraControlsRef.current;
      const zoomSpeed = 0.002;
      const delta = event.deltaY;
      const zoomFactor = Math.exp(delta * zoomSpeed);
      const newDistance = Math.max(10, controls.currentDistance * zoomFactor);
      controls.currentDistance = newDistance;
      updateCameraPosition(currentRotationRef.current, newDistance);
    };

    // ── TOUCH HANDLERS ───────────────────────────────────────────────────────
    const handleTouchStart = (event: TouchEvent) => {
      lastInteractionRef.current = Date.now();
      const controls = cameraControlsRef.current;

      if (event.touches.length === 1) {
        touchStateRef_local.isTouching = true;
        touchStateRef_local.lastTouchPosition = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
        };
        touchStateRef_local.touchStartPosition = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
        };
        touchStateRef_local.hasMoved = false;
        touchStateRef_local.touchStartTime = Date.now();
        controls.rotationVelocity = { x: 0, y: 0 };
      } else if (event.touches.length === 2) {
        event.preventDefault();
        touchStateRef_local.isPinching = true;
        touchStateRef_local.isTouching = false;
        const dx = event.touches[0].clientX - event.touches[1].clientX;
        const dy = event.touches[0].clientY - event.touches[1].clientY;
        touchStateRef_local.lastPinchDistance = Math.sqrt(dx * dx + dy * dy);
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      const controls = cameraControlsRef.current;

      if (touchStateRef_local.isTouching && event.touches.length === 1) {
        const deltaX =
          event.touches[0].clientX - touchStateRef_local.lastTouchPosition.x;
        const deltaY =
          event.touches[0].clientY - touchStateRef_local.lastTouchPosition.y;
        const totalMovement = Math.abs(deltaX) + Math.abs(deltaY);

        if (totalMovement > 5) {
          event.preventDefault();
          touchStateRef_local.hasMoved = true;

          const rotationSpeed = 0.5;
          const deltaRotationX = deltaY * rotationSpeed;
          const deltaRotationY = -deltaX * rotationSpeed;

          controls.rotationVelocity.x = deltaRotationX * 0.1;
          controls.rotationVelocity.y = deltaRotationY * 0.1;

          const newRotation = {
            x: currentRotationRef.current.x + deltaRotationX,
            y: currentRotationRef.current.y + deltaRotationY,
          };

          animationRef.current.isAnimating = false;
          updateCameraPosition(newRotation, controls.currentDistance);
          updateNaviCube(newRotation);
          setCameraRotation(newRotation);

          touchStateRef_local.lastTouchPosition = {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY,
          };
        }
      } else if (touchStateRef_local.isPinching && event.touches.length === 2) {
        event.preventDefault();
        const dx = event.touches[0].clientX - event.touches[1].clientX;
        const dy = event.touches[0].clientY - event.touches[1].clientY;
        const currentDistance = Math.sqrt(dx * dx + dy * dy);
        const delta = currentDistance - touchStateRef_local.lastPinchDistance;
        const zoomSpeed = 0.01;

        const newDistance = Math.max(
          10,
          controls.currentDistance -
            delta * zoomSpeed * controls.currentDistance,
        );
        controls.currentDistance = newDistance;
        updateCameraPosition(currentRotationRef.current, newDistance);

        touchStateRef_local.lastPinchDistance = currentDistance;
      }
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (event.touches.length === 0) {
        touchStateRef_local.isTouching = false;
        touchStateRef_local.isPinching = false;
        touchStateRef_local.hasMoved = false;
      } else if (event.touches.length === 1 && touchStateRef_local.isPinching) {
        touchStateRef_local.isPinching = false;
        touchStateRef_local.isTouching = true;
        touchStateRef_local.lastTouchPosition = {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY,
        };
        touchStateRef_local.hasMoved = false;
        touchStateRef_local.touchStartTime = Date.now();
      }
    };

    // ── REGISTER LISTENERS ───────────────────────────────────────────────────
    const container = mountRef.current;
    container.addEventListener("mousedown", handleMouseDown);
    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("click", handleClick);
    container.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("mouseup", handleMouseUp);
    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd);

    // ── CLEANUP ──────────────────────────────────────────────────────────────
    return () => {
      container?.removeEventListener("mousedown", handleMouseDown);
      container?.removeEventListener("mousemove", handleMouseMove);
      container?.removeEventListener("click", handleClick);
      container?.removeEventListener("wheel", handleWheel);
      window.removeEventListener("mouseup", handleMouseUp);
      container?.removeEventListener("touchstart", handleTouchStart);
      container?.removeEventListener("touchmove", handleTouchMove);
      container?.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    isHovering,
    updateCameraPosition,
    updateNaviCube,
    createEdgeVisual,
    createFaceVisual,
    clearHoverVisual,
  ]);

  return (
    <div className={styles.heroView}>
      <div
        ref={mountRef}
        className={styles.viewport}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      />

      {/* NaviCube with full interactivity */}
      <div className={styles.naviCube}>
        <div
          ref={naviCubeRef}
          className={styles.cube}
          style={{
            transform: `rotateX(${cameraRotation.x}deg) rotateY(${cameraRotation.y}deg)`,
          }}
        >
          {renderFace("front", "frontFace")}
          {renderFace("back", "backFace")}
          {renderFace("right", "rightFace")}
          {renderFace("left", "leftFace")}
          {renderFace("top", "topFace")}
          {renderFace("bottom", "bottomFace")}
        </div>
      </div>

      {/* Color picker button (non-functional, just visual) */}
      <div className={styles.bottomLeft}>
        <div className={styles.colorPickerContainer}>
          <button className={styles.colorButton} onClick={onNavigateToSignUp}>
            <div
              className={styles.colorSquare}
              style={{
                backgroundColor: theme === "dark" ? "#6f6f6f" : "#8d8d8d",
              }}
            />
            <div className={styles.hashtag}>#</div>
            <div className={styles.hex}>8D8D8D</div>
          </button>
        </div>
      </div>

      {/* Download button (non-functional, just visual) */}
      <div className={styles.bottomRight}>
        <div className={styles.downloadContainer}>
          <button className={styles.shareButton} onClick={onNavigateToSignUp}>
            <img src={shareIcon} alt="Share" className={styles.buttonIcon} />
            <span className={styles.shareText}>Share</span>
          </button>
        </div>
        <div className={styles.downloadContainer}>
          <button
            className={styles.downloadButton}
            onClick={onNavigateToSignUp}
          >
            <img
              src={exportIcon}
              alt="Download"
              className={styles.buttonIcon}
            />
            <span className={styles.buttonText}>Download</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeroView;
